import { useQuery } from "@tanstack/react-query";
import { LineChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid } from "recharts";
import { api, config } from "../lib/api.js";
import { PageHeader } from "../components/PageHeader.jsx";
import { EmptyState } from "../components/EmptyState.jsx";

export function Overview() {
  const { data, isLoading, error, dataUpdatedAt } = useQuery({
    queryKey: ["overview"],
    queryFn: api.overview,
    refetchInterval: 60_000,
    enabled: config.hasBase
  });

  if (!config.hasBase) {
    return (
      <>
        <PageHeader title="Overview" />
        <EmptyState
          title="Backend not configured"
          description="Set VITE_API_BASE and VITE_API_TOKEN in dashboard/.env.local, then restart the dev server."
        />
      </>
    );
  }

  if (isLoading) return <div className="subtle">Loading…</div>;
  if (error) return <EmptyState title="Failed to load" description={String(error.message)} />;

  const followers = data?.followers || { current: 0, delta: 0, series: [] };
  const trending = data?.trending || [];
  const recentPosts = data?.myRecentPosts || [];
  const recentIssues = data?.recentIssues || [];

  return (
    <>
      <PageHeader
        title="Overview"
        subtitle={`Last sync: ${dataUpdatedAt ? new Date(dataUpdatedAt).toLocaleTimeString() : "–"}`}
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Kpi label="Followers" value={followers.current.toLocaleString()} delta={followers.delta} />
        <Kpi label="Posts captured (mine)" value={recentPosts.length} />
        <Kpi label="Trending hashtags (24h)" value={trending.length} />
      </div>

      <div className="card mb-6">
        <div className="flex items-center justify-between mb-3">
          <div className="font-medium">Followers — last 14 snapshots</div>
        </div>
        <div className="h-64">
          {followers.series?.length ? (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={followers.series}>
                <CartesianGrid stroke="#1f2630" strokeDasharray="3 3" />
                <XAxis dataKey="snapshot_at" tickFormatter={fmtDate} stroke="#6b7280" />
                <YAxis stroke="#6b7280" allowDecimals={false} />
                <Tooltip contentStyle={tooltipStyle} labelFormatter={fmtDate} />
                <Line type="monotone" dataKey="total" stroke="#0a66c2" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="subtle text-center pt-16">No follower snapshots yet. Visit your LinkedIn dashboard with the extension installed.</div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="card">
          <div className="font-medium mb-2">Trending hashtags (24h)</div>
          {trending.length === 0 ? (
            <div className="subtle text-sm">Browse your LinkedIn feed to capture trending hashtags.</div>
          ) : (
            <ul className="space-y-1">
              {trending.slice(0, 12).map((t) => (
                <li key={t.hashtag} className="flex justify-between text-sm">
                  <span>#{t.hashtag}</span>
                  <span className="subtle">{t.total}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="card">
          <div className="font-medium mb-2">My recent posts</div>
          {recentPosts.length === 0 ? (
            <div className="subtle text-sm">Visit your own LinkedIn profile to backfill your posts.</div>
          ) : (
            <ul className="space-y-2">
              {recentPosts.slice(0, 5).map((p) => (
                <li key={p.urn} className="text-sm">
                  <div className="line-clamp-2">{p.body}</div>
                  <div className="subtle text-xs mt-1">
                    {p.like_count} likes · {p.comment_count} comments
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="card md:col-span-2">
          <div className="font-medium mb-2">Recent newsletter issues</div>
          {recentIssues.length === 0 ? (
            <div className="subtle text-sm">No issues captured yet.</div>
          ) : (
            <ul className="space-y-1 text-sm">
              {recentIssues.map((i) => (
                <li key={i.title} className="flex justify-between">
                  <span>{i.name}: {i.title}</span>
                  <span className="subtle">{i.like_count || 0} likes</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </>
  );
}

function Kpi({ label, value, delta }) {
  const showDelta = typeof delta === "number" && delta !== 0;
  return (
    <div className="card">
      <div className="label">{label}</div>
      <div className="kpi-num mt-1">{value}</div>
      {showDelta && (
        <div className={`text-xs mt-1 ${delta >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
          {delta >= 0 ? "+" : ""}{delta} since first snapshot
        </div>
      )}
    </div>
  );
}

const tooltipStyle = { background: "#0e1119", border: "1px solid #1f2630", color: "#fff" };

function fmtDate(s) {
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleDateString();
}
